/**
 * JSON Import / Export ユーティリティ。
 *
 * - Export: 現在の放送地点一覧を JSON ファイルとしてダウンロードする。
 * - Import: ファイルを読み込んで `BroadcastPoint[]` を返す (バリデーションは呼び出し元で行う)。
 *
 * 本タスクで `contents[]` に対応した。トップレベルの `delay_threshold_seconds`
 * (旧仕様) を持つレガシー JSON も受け取れるようにし、import 時に「自列車遅延」
 * コンテンツへ変換した上で `contents` に設定する。
 */
import type { BroadcastContent, BroadcastPoint, LanguageVoiceSetting } from '../api/types';
import { normalizeContents } from './broadcastContents';

export interface ExportPayload {
  exportedAt: string;
  count: number;
  items: BroadcastPoint[];
}

/** 一覧を JSON ファイルとしてダウンロードする (ブラウザのみで動作) */
export function exportBroadcastPointsAsJson(
  items: BroadcastPoint[],
  filename = 'broadcast-points-export.json',
): void {
  const payload: ExportPayload = {
    exportedAt: new Date().toISOString(),
    count: items.length,
    items,
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Import 用 JSON のパース。トップレベルが配列でも `{items: [...]}` でも受け付ける。 */
export function parseImportJson(text: string): BroadcastPoint[] {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (e) {
    // 元エラーは `cause` として引き継ぎ、デバッグ時にスタックトレースを追跡できるようにする
    // (ESLint ルール `preserve-caught-error` に準拠)。
    throw new Error(`JSON のパースに失敗しました: ${(e as Error).message}`, { cause: e });
  }

  let items: unknown;
  if (Array.isArray(raw)) {
    items = raw;
  } else if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    items = obj.items ?? obj.broadcastSpots ?? obj.broadcastPoints;
  }

  if (!Array.isArray(items)) {
    throw new Error('JSON は配列、または `items` プロパティを含むオブジェクトでなければいけません');
  }

  return items.map((item, index) => {
    if (!item || typeof item !== 'object') {
      throw new Error(`items[${index}] がオブジェクトではありません`);
    }
    const obj = item as Record<string, unknown>;
    if (typeof obj.routeDirection !== 'string' || !obj.routeDirection) {
      throw new Error(`items[${index}].routeDirection が必須です`);
    }

    // contents の正規化: 明示的に与えられた配列を優先。
    // 与えられていない場合はレガシーフィールド (delay_threshold_seconds) を
    // 「自列車遅延」コンテンツへ変換する (import 時にレガシー → 新仕様へ片寄せ)。
    let contents: BroadcastContent[];
    if (Array.isArray(obj.contents)) {
      contents = normalizeContents(obj.contents);
    } else if (typeof obj.delay_threshold_seconds === 'number') {
      contents = [
        {
          category: 'self_train_delay',
          properties: { delay_threshold_seconds: obj.delay_threshold_seconds },
        },
      ];
    } else {
      contents = [];
    }

    return {
      routeDirection: obj.routeDirection,
      spotId: typeof obj.spotId === 'string' ? obj.spotId : '',
      description: typeof obj.description === 'string' ? obj.description : undefined,
      latitude: typeof obj.latitude === 'number' ? obj.latitude : undefined,
      longitude: typeof obj.longitude === 'number' ? obj.longitude : undefined,
      radius_meters: typeof obj.radius_meters === 'number' ? obj.radius_meters : undefined,
      broadcast_delay_seconds:
        typeof obj.broadcast_delay_seconds === 'number' ? obj.broadcast_delay_seconds : undefined,
      // Issue #639: Polly の音声設定。Export した JSON を Import しても話者が消えないよう保持する
      // (以前は voice_id を引き継いでおらず、Import すると既定話者に戻っていた)。
      ...(typeof obj.voice_id === 'string' && obj.voice_id.trim() !== ''
        ? { voice_id: obj.voice_id.trim() }
        : {}),
      ...(obj.voice_settings && typeof obj.voice_settings === 'object'
        ? { voice_settings: obj.voice_settings as Record<string, LanguageVoiceSetting> }
        : {}),
      // Issue #679: 無効 (false) のときだけ保持する (未指定 = 有効)。
      ...(obj.enabled === false ? { enabled: false } : {}),
      contents,
    } satisfies BroadcastPoint;
  });
}
